import React, { useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle, X } from "lucide-react";
import { C, FONT } from "../styles";

interface BankConnectPanelProps {
  onClose: () => void;
}

type Country = "CA" | "US" | "GB" | "AU" | "other";

function StepDot({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  const bg = done ? C.green : active ? C.teal : C.line;
  const fg = done || active ? "#fff" : C.muted;
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        fontSize: 13,
        fontWeight: 600,
        flex: "0 0 28px",
        background: bg,
        color: fg,
        transition: "0.18s",
      }}
    >
      {done ? <CheckCircle size={14} /> : n}
    </div>
  );
}

export default function BankConnectPanel({ onClose }: BankConnectPanelProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [country, setCountry] = useState<Country>("CA");
  const [bankName, setBankName] = useState("");
  const [acctName, setAcctName] = useState("");
  const [transit, setTransit] = useState("");
  const [inst, setInst] = useState("");
  const [routing, setRouting] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [bsb, setBsb] = useState("");
  const [iban, setIban] = useState("");
  const [acct, setAcct] = useState("");

  const next = () => {
    if (step === 1) {
      if (!bankName.trim() || !acctName.trim()) {
        alert("Please fill in your bank and account holder name.");
        return;
      }
      setStep(2);
    } else {
      onClose();
    }
  };
  const back = () => {
    if (step === 1) onClose();
    else setStep(1);
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    border: "1px solid " + C.line,
    borderRadius: 11,
    padding: "12px 14px",
    fontFamily: FONT,
    fontSize: 14,
    color: C.ink,
    outline: "none",
  };
  const labelStyle: CSSProperties = {
    fontSize: 12.5,
    fontWeight: 600,
    color: C.ink,
    marginBottom: 6,
    display: "block",
  };

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(16,26,43,0.42)", zIndex: 1000 }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          maxHeight: "100vh",
          width: "min(440px, 96vw)",
          background: "#fff",
          boxShadow: "-12px 0 40px rgba(16,26,43,0.18)",
          display: "flex",
          flexDirection: "column",
          zIndex: 1001,
          fontFamily: FONT,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid " + C.line,
          }}
        >
          <h3 style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>
            {step === 1 ? "Connect your bank" : "Verify your account"}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: C.muted,
              padding: 4,
              display: "inline-flex",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            maxHeight: "calc(100vh - 140px)",
            padding: "18px 22px 8px",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
            <StepDot n={1} active={step === 1} done={step > 1} />
            <div style={{ flex: 1, height: 2, background: step > 1 ? C.green : C.line }} />
            <StepDot n={2} active={step === 2} done={false} />
          </div>

          {step === 1 && (
            <>
              <div style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginBottom: 6 }}>
                Your bank details
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
                Enter the business chequing account payroll will draw from. We use bank-level
                encryption to keep this secure.
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value as Country)}
                  style={inputStyle}
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Bank name</label>
                <input
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="For example, Royal Bank of Canada"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Account holder name</label>
                <input
                  value={acctName}
                  onChange={(e) => setAcctName(e.target.value)}
                  placeholder="Your business name"
                  style={inputStyle}
                />
              </div>

              {country === "CA" && (
                <>
                  <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Transit number</label>
                      <input
                        value={transit}
                        onChange={(e) => setTransit(e.target.value)}
                        placeholder="00000"
                        maxLength={5}
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Institution number</label>
                      <input
                        value={inst}
                        onChange={(e) => setInst(e.target.value)}
                        placeholder="000"
                        maxLength={3}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Account number</label>
                    <input
                      value={acct}
                      onChange={(e) => setAcct(e.target.value)}
                      placeholder="0000000"
                      type="password"
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
              {country === "US" && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Routing number (9 digits)</label>
                    <input
                      value={routing}
                      onChange={(e) => setRouting(e.target.value)}
                      placeholder="000000000"
                      maxLength={9}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Account number</label>
                    <input
                      value={acct}
                      onChange={(e) => setAcct(e.target.value)}
                      placeholder="0000000"
                      type="password"
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
              {country === "GB" && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Sort code</label>
                    <input
                      value={sortCode}
                      onChange={(e) => setSortCode(e.target.value)}
                      placeholder="00-00-00"
                      maxLength={8}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Account number (8 digits)</label>
                    <input
                      value={acct}
                      onChange={(e) => setAcct(e.target.value)}
                      placeholder="00000000"
                      maxLength={8}
                      type="password"
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
              {country === "AU" && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>BSB number</label>
                    <input
                      value={bsb}
                      onChange={(e) => setBsb(e.target.value)}
                      placeholder="000-000"
                      maxLength={7}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Account number</label>
                    <input
                      value={acct}
                      onChange={(e) => setAcct(e.target.value)}
                      placeholder="00000000"
                      type="password"
                      style={inputStyle}
                    />
                  </div>
                </>
              )}
              {country === "other" && (
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>IBAN or account identifier</label>
                  <input
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder="Enter your account identifier"
                    style={inputStyle}
                  />
                </div>
              )}

              <div
                style={{
                  background: C.tealSoft,
                  borderRadius: 10,
                  padding: "12px 14px",
                  fontSize: 12.5,
                  color: C.tealInk,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  marginTop: 8,
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 2 }}>🔒</span>
                <span>
                  Your information is encrypted and never shared. Used only to pay your
                  employees.
                </span>
              </div>
            </>
          )}

          {step === 2 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: C.greenSoft,
                  color: C.green,
                  display: "grid",
                  placeItems: "center",
                  margin: "0 auto 16px",
                }}
              >
                <CheckCircle size={32} />
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: C.ink,
                  marginBottom: 8,
                }}
              >
                Almost there
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: C.muted,
                  marginBottom: 20,
                  maxWidth: 340,
                  marginLeft: "auto",
                  marginRight: "auto",
                  lineHeight: 1.6,
                }}
              >
                We will deposit a small amount (less than $1.00) into your account within 1 to 2
                business days. Come back and confirm the amount to finish setup.
              </div>
              <div
                style={{
                  background: C.amberSoft,
                  borderRadius: 10,
                  padding: 14,
                  fontSize: 12.5,
                  color: C.amber,
                  textAlign: "left",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Until verification is complete, pay employees by cheque.</span>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 22px",
            borderTop: "1px solid " + C.line,
          }}
        >
          <button
            onClick={back}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              fontWeight: 600,
              padding: "8px 4px",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 14,
            }}
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={next}
            style={{
              background: C.teal,
              color: "#fff",
              border: "none",
              borderRadius: 11,
              padding: "10px 22px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: FONT,
              boxShadow: "0 2px 8px rgba(21,160,140,0.28)",
            }}
          >
            {step === 2 ? "Done" : "Continue"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}